import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { clearToken } from "../lib/authToken";
import { setPreferredTz, deviceTz } from "../lib/datetime";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Sync display timezone: use the user's saved zone, or persist their device zone on first sight
      // so appointment reminders can be phrased in their local time.
      if (data && data.role) {
        if (data.timezone) {
          setPreferredTz(data.timezone);
        } else {
          const tz = deviceTz();
          setPreferredTz(tz);
          api.put("/me/timezone", { timezone: tz }).catch(() => {});
        }
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (e) { console.error("Logout request failed (clearing local session anyway):", e); }
    clearToken();
    setUser(false);
    window.location.href = "/";
  }, []);

  // Auto-logout after 15 minutes of inactivity (only while signed in).
  useEffect(() => {
    if (!user) return;
    const IDLE_MS = 15 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { logout(); }, IDLE_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  const value = useMemo(
    () => ({ user, setUser, loading, checkAuth, logout }),
    [user, loading, checkAuth, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
